import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../../Services/AuthService';
import axios from 'axios';
import { getUser } from '../../Services/LondunarkerfiAPIService';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { HeaderWrapper, LoaderWrapper, RouteWithAuthWrapper } from './RouteWithAuth.styled';
import Header from '../Header/Header';
import User from '../../Models/UserModels/User';

interface RouteWithAuthProps {
  component: React.ComponentType<{ user: User, darkMode: boolean, setDarkMode: (darkMode: boolean) => void }>;
  reWeighting: boolean;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

export default function RouteWithAuth({ component: Component, reWeighting, darkMode, setDarkMode }: RouteWithAuthProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authorizationError, setAuthorizationError] = useState('');
  const [needsLogin, setNeedsLogin] = useState(false);
  const sessionExpired = useSyncExternalStore(authService.subscribe, authService.isSessionExpired);
  const location = useLocation();

  const checkAuthorization = useCallback(async () => {
    setIsLoading(true);
    setAuthorizationError('');
    try {
      const oicdUser = await authService.getUser();
      if (!oicdUser && !authService.isSessionExpired()) {
        setNeedsLogin(true);
        return;
      }
      const userData = await getUser();
      if (!userData || (reWeighting && !userData.isReweighingUser) || (!reWeighting && userData.isReweighingUser)) {
        // Unauthorized user
        setIsLoading(false);
        setIsAuthenticated(false);
        return;
      }
      // Authorized user
      setUser(userData);
      setIsAuthenticated(true);
    } catch (err) {
      setIsAuthenticated(false);
      if (!axios.isAxiosError(err) || err.response?.status !== 403) {
        setAuthorizationError('Ekki tókst að staðfesta aðgang. Reyndu aftur þegar samband hefur náðst eða innskráningu er lokið.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [reWeighting]);

  useEffect(() => {
    // Endurheimtum fyrstu aðgangsskoðun eftir innskráningu án þess að afmá opin form.
    if (!sessionExpired && !user) void checkAuthorization();
  }, [checkAuthorization, sessionExpired, user]);

  if (needsLogin) return <Navigate to="/" replace />;

  if (isLoading) {
    return (
      <RouteWithAuthWrapper>
        <HeaderWrapper>
          <Header isAuthorized={true} />
        </HeaderWrapper>
        <LoaderWrapper>
          <Box sx={{ display: 'flex' }}>
            <CircularProgress />
          </Box>
        </LoaderWrapper>
      </RouteWithAuthWrapper>
    );
  }

  if (authorizationError) {
    return <RouteWithAuthWrapper>
      <Header isAuthorized={false} />
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{authorizationError}</Alert>
        <Button onClick={checkAuthorization} sx={{ mt: 2 }}>Reyna aftur</Button>
      </Box>
    </RouteWithAuthWrapper>;
  }

  if (!isAuthenticated || (user && reWeighting !== user.isReweighingUser)) {
    // If unauthorized, navigate to /unauthorized
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  return (
    <RouteWithAuthWrapper>
      {user && (
        <Component user={user} darkMode={darkMode} setDarkMode={setDarkMode} />
      )}
    </RouteWithAuthWrapper>
  );
}
